/*! @wharfkit/wallet-plugin-metamask v4.0.0-rc7 | BSD-3-Clause | Copyright (c) 2024 Greymass Inc. | https://github.com/wharfkit/js */
import { AbstractWalletPlugin, Logo, PublicKey, Signature, WalletPluginMetadata, cancelable } from "@wharfkit/session";
//#region src/metamask.ts
async function hasSnapsSupport(provider = window.ethereum) {
	try {
		await provider.request({ method: "wallet_getSnaps" });
		return true;
	} catch {
		return false;
	}
}
async function getMetaMaskEIP6963Provider() {
	return new Promise((rawResolve) => {
		const timeout = setTimeout(() => {
			resolve(null);
		}, 500);
		function resolve(provider) {
			window.removeEventListener("eip6963:announceProvider", onAnnounceProvider);
			clearTimeout(timeout);
			rawResolve(provider);
		}
		function onAnnounceProvider({ detail }) {
			const { info, provider } = detail;
			if (info.rdns.includes("io.metamask")) resolve(provider);
		}
		window.addEventListener("eip6963:announceProvider", onAnnounceProvider);
		window.dispatchEvent(new Event("eip6963:requestProvider"));
	});
}
async function getSnapsProvider() {
	if (typeof window === "undefined") return null;
	if (await hasSnapsSupport()) return window.ethereum;
	if (window.ethereum?.detected) {
		for (const provider of window.ethereum.detected) if (await hasSnapsSupport(provider)) return provider;
	}
	if (window.ethereum?.providers) {
		for (const provider of window.ethereum.providers) if (await hasSnapsSupport(provider)) return provider;
	}
	const eip6963Provider = await getMetaMaskEIP6963Provider();
	if (eip6963Provider && await hasSnapsSupport(eip6963Provider)) return eip6963Provider;
	return null;
}
async function checkIsFlask(provider) {
	if (!provider) return false;
	return (await provider.request({ method: "web3_clientVersion" }))?.includes("flask");
}
//#endregion
//#region src/snaps.ts
const snapOrigins = new Map([
	["eos", "npm:@greymass/eos-wallet"],
	["jungle4", "npm:vaulta-wallet"],
	["vaulta", "npm:vaulta-wallet"]
]);
//#endregion
//#region src/index.ts
const defaultSnapOrigin = "npm:@greymass/eos-wallet";
const defaultSetupPageUrl = "https://unicove.com/metamask";
const logo = "data:image/svg+xml;base64,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";
var WalletPluginMetaMask = class extends AbstractWalletPlugin {
	constructor(walletPluginMetaMaskConfig) {
		super();
		this.id = "wallet-plugin-metamask";
		this.config = {
			requiresChainSelect: true,
			requiresPermissionSelect: true
		};
		this.metadata = WalletPluginMetadata.from({
			name: "MetaMask",
			description: "",
			logo: Logo.from({
				light: logo,
				dark: logo
			}),
			homepage: "https://unicove.com/metamask",
			download: "https://unicove.com/metamask"
		});
		this.installedSnap = null;
		this.provider = null;
		this.isFlask = false;
		this.snapOrigin = walletPluginMetaMaskConfig?.snapOrigin || defaultSnapOrigin;
		this.setupPageUrl = walletPluginMetaMaskConfig?.setupPageUrl || defaultSetupPageUrl;
	}
	login(context) {
		const promise = this.metamaskLogin(context);
		return cancelable(promise, (canceled) => {
			throw canceled;
		});
	}
	async metamaskLogin(context) {
		await this.initialize(context);
		if (!this.provider) throw new Error("Metamask not found");
		let chain;
		if (context.chain) chain = context.chain.id;
		else chain = context.chains[0].id;
		if (!context.permissionLevel) throw new Error("Calling login() without a permissionLevel is not supported by the WalletPluginMetaMask plugin.");
		return {
			chain,
			permissionLevel: context.permissionLevel
		};
	}
	sign(resolved) {
		const promise = this.metamaskSign(resolved);
		return cancelable(promise, (canceled) => {
			throw canceled;
		});
	}
	async metamaskSign(resolved) {
		await this.initialize();
		const result = await this.invokeSnap({
			method: "antelope_signTransaction",
			params: {
				chainId: String(resolved.chainId),
				transaction: JSON.stringify(resolved.transaction)
			}
		});
		if (!result) throw new Error("No result returned");
		return { signatures: [Signature.from(result)] };
	}
	async retrievePublicKey(chainId) {
		await this.initialize();
		const result = await this.invokeSnap({
			method: "antelope_getActivePublicKey",
			params: { chainId: String(chainId) }
		});
		return PublicKey.from(result);
	}
	async request({ method, params }) {
		if (!this.provider) throw new Error("Snap provider not found");
		return await this.provider.request({
			method,
			params
		}) ?? null;
	}
	async initialize(context) {
		try {
			if (!this.provider) this.provider = await getSnapsProvider();
			if (!this.provider) throw new Error("Metamask not found");
			this.isFlask = await checkIsFlask(this.provider);
			await this.requestSnap();
			if (this.provider && !this.installedSnap) context?.ui?.prompt({
				title: "Antelope Snap Setup Required",
				body: `
                        It looks like the Antelope snap for MetaMask isn't installed yet.

                        Click the button below to go to our setup page:
                    `,
				elements: [{
					type: "button",
					label: "Go to Setup Page",
					data: { onClick: () => {
						window.open(this.setupPageUrl, "_blank");
					} }
				}]
			});
		} catch (error) {
			throw new Error(error.message ?? "Failed to initialize MetaMask");
		}
	}
	async setSnap() {
		const snaps = await this.request({
			method: "wallet_getSnaps",
			params: {}
		});
		this.installedSnap = snaps[this.snapOrigin] ?? null;
	}
	async requestSnap(id, version) {
		const snapId = id || this.snapOrigin;
		const snaps = await this.request({
			method: "wallet_requestSnaps",
			params: { [snapId]: version ? { version } : {} }
		});
		this.installedSnap = snaps?.[snapId] ?? null;
	}
	async invokeSnap({ method, params }, id) {
		const snapId = id || this.snapOrigin;
		return this.request({
			method: "wallet_invokeSnap",
			params: {
				snapId,
				request: {
					method,
					params
				}
			}
		});
	}
};
//#endregion
export { WalletPluginMetaMask, checkIsFlask, getMetaMaskEIP6963Provider, getSnapsProvider, hasSnapsSupport, snapOrigins };

//# sourceMappingURL=wallet-plugin-metamask.m.js.map